import axios from 'axios';
import type { AxiosInstance, InternalAxiosRequestConfig, AxiosError, AxiosHeaders } from 'axios';
import { b2cAuthService } from './b2cAuthService';
import { UPGRADE_REQUIRED_EVENT } from '../contexts/UpgradeModalContext';
import { PAYMENT_METHOD_REQUIRED_EVENT } from '../contexts/PaymentMethodModalContext';
import { SUPPRESS_UPGRADE_MODAL_HEADER } from '../constants/apiHeaders';
import { PaymentMethodErrorCodes } from '../constants/paymentMethodErrorCodes';
import { AccountErrorCodes } from '../constants/accountErrorCodes';
import {
  getAccountDisabledErrorCode,
  stashAccountDisabledBanner,
} from './accountDisabledFlow';

/**
 * API Client Configuration
 *
 * Best Practice: Use build-time environment variables (VITE_API_URL) set in the pipeline.
 * This is the standard approach for SPAs - URLs are configured per environment at build time.
 *
 * Fallback order:
 * 1. Build-time env var (VITE_API_URL) - set in pipeline for each environment
 * 2. Relative path (/api) - works for local development or same-origin deployments
 *
 * Custom domain URLs:
 * - Dev: https://api-dev.scheduledsmiles.com/api
 * - Prod: https://api.scheduledsmiles.com/api
 *
 * Token Refresh Logic:
 * - On 401 response, attempts to refresh the token silently using MSAL
 * - If refresh succeeds, retries the original request with the new token
 * - If refresh fails, triggers auth expiration callback to redirect to login
 */
const apiClient: AxiosInstance = axios.create({
  // Use build-time env var if available (set in pipeline), otherwise default to relative path
  baseURL: import.meta.env.VITE_API_URL || '/api',
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true, // Important for httpOnly cookies
});

/**
 * Suppression counter for the 402 Payment Required interceptor.
 * While > 0, the interceptor still rejects with the error but does NOT
 * dispatch paymentMethod:required, upgrade:required, or subscription:expired events.
 *
 * Used by retry-after-upgrade / retry-after-card flows to prevent global modals
 * from re-opening while an in-flight retry is in progress.
 */
let _suppress402Count = 0;

/**
 * Wraps an async API call so that any 402 responses do NOT trigger global
 * payment-method or upgrade modals. The 402 error is still rejected normally
 * so the caller can handle it, but modal listeners are suppressed.
 */
export async function withSuppressed402<T>(fn: () => Promise<T>): Promise<T> {
  _suppress402Count++;
  try {
    return await fn();
  } finally {
    _suppress402Count--;
  }
}

/** Flag to prevent multiple simultaneous token refresh attempts from interceptor */
let isRefreshingToken = false;

/** Queue of failed requests to retry after token refresh */
let failedRequestsQueue: Array<{
  resolve: (value: unknown) => void;
  reject: (error: unknown) => void;
  config: InternalAxiosRequestConfig;
}> = [];

/**
 * Process all queued requests after token refresh.
 * Retries each request with the new token or rejects if refresh failed.
 *
 * @param token New token if refresh succeeded, null if failed
 */
const processQueue = (token: string | null): void => {
  failedRequestsQueue.forEach(({ resolve, reject, config }) => {
    if (token) {
      // Update the request with new token and retry
      config.headers.Authorization = `Bearer ${token}`;
      resolve(apiClient(config));
    } else {
      // Refresh failed, reject the request
      reject(new Error('Session expired. Please log in again.'));
    }
  });
  failedRequestsQueue = [];
};

/**
 * Request interceptor to add auth token.
 * Uses getValidToken which will refresh proactively if token is about to expire.
 */
apiClient.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    // Skip token handling for auth config endpoint (needed during initialization)
    if (config.url?.includes('/auth/config')) {
      return config;
    }

    // Try to get a valid token (will refresh proactively if near expiry)
    const token = await b2cAuthService.getValidToken();

    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

/**
 * Custom event dispatched when the API returns 402 Payment Required.
 * The SubscriptionContext listens for this to trigger a status refresh.
 */
export const SUBSCRIPTION_EXPIRED_EVENT = 'subscription:expired';

/**
 * Returns true when the outgoing request asked the 402 interceptor to stay silent
 * (no UpgradeModal / subscription refresh events). Used for soft checks that
 * render an inline upsell instead of a blocking modal.
 */
/**
 * Reads errorCode from a 402 response body (camelCase or PascalCase).
 */
function getPaymentRequiredErrorCode(data: unknown): string | undefined {
  if (!data || typeof data !== 'object') return undefined;
  const body = data as { errorCode?: unknown; ErrorCode?: unknown };
  const raw = body.errorCode ?? body.ErrorCode;
  return typeof raw === 'string' ? raw : undefined;
}

function isSuppressUpgradeModalHeader(config: InternalAxiosRequestConfig | undefined): boolean {
  if (!config?.headers) return false;
  const h = config.headers;
  const raw =
    typeof (h as AxiosHeaders).get === 'function'
      ? (h as AxiosHeaders).get(SUPPRESS_UPGRADE_MODAL_HEADER) ??
        (h as AxiosHeaders).get(SUPPRESS_UPGRADE_MODAL_HEADER.toLowerCase())
      : (h as Record<string, unknown>)[SUPPRESS_UPGRADE_MODAL_HEADER] ??
        (h as Record<string, unknown>)[SUPPRESS_UPGRADE_MODAL_HEADER.toLowerCase()];
  return raw === true || raw === 'true' || raw === '1';
}

/**
 * Response interceptor to handle errors including:
 * - 401 Unauthorized: Attempts token refresh
 * - 402 Payment Required: Signals subscription expiry to the frontend
 * - 429 Too Many Requests: Handles rate limiting with retry info
 *
 * Business Logic:
 * 1. On 401, attempt to refresh the token silently
 * 2. If refresh succeeds, retry the original request
 * 3. If refresh fails, notify auth context to redirect to login
 * 4. Queue multiple requests during refresh to avoid race conditions
 * 5. On 402, dispatch a custom event so SubscriptionContext can refresh
 * 6. On 429, provide user-friendly error with retry timing
 */
apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

    // Handle 402 Payment Required — subscription feature gate or card-on-file precondition.
    // Events are suppressed while withSuppressed402() is active (e.g. during retry flows).
    if (error.response?.status === 402) {
      if (_suppress402Count === 0 && !isSuppressUpgradeModalHeader(originalRequest)) {
        const responseData = error.response.data;
        const reason = (responseData as { message?: string })?.message;
        const errorCode = getPaymentRequiredErrorCode(responseData);

        if (errorCode === PaymentMethodErrorCodes.PaymentMethodRequired) {
          window.dispatchEvent(
            new CustomEvent(PAYMENT_METHOD_REQUIRED_EVENT, { detail: { reason } })
          );
        } else {
          window.dispatchEvent(new CustomEvent(SUBSCRIPTION_EXPIRED_EVENT));
          window.dispatchEvent(
            new CustomEvent(UPGRADE_REQUIRED_EVENT, { detail: { reason } })
          );
        }
      }
      return Promise.reject(error);
    }

    // Handle 403 account disabled before any 401 token refresh (PBI #1230).
    if (error.response?.status === 403) {
      const errorCode = getAccountDisabledErrorCode(error.response.data);
      if (errorCode === AccountErrorCodes.AccountDisabled) {
        const reason = (error.response.data as { message?: string })?.message;
        stashAccountDisabledBanner(
          reason ??
            'Your account has been disabled. If you believe this is a mistake, contact support.',
        );
        return Promise.reject(error);
      }
    }

    // Handle 429 Rate Limit errors
    if (error.response?.status === 429) {
      const retryAfter = error.response.headers['retry-after'];
      const retrySeconds = retryAfter ? parseInt(retryAfter, 10) : 60;

      console.warn(`[apiClient] Rate limited. Retry after ${retrySeconds} seconds.`);

      // Create a user-friendly error with rate limit info
      const rateLimitError = new Error(
        `Too many requests. Please wait ${retrySeconds} seconds before trying again.`
      ) as Error & {
        isRateLimited: boolean;
        retryAfterSeconds: number;
        originalError: AxiosError;
      };
      rateLimitError.isRateLimited = true;
      rateLimitError.retryAfterSeconds = retrySeconds;
      rateLimitError.originalError = error;

      return Promise.reject(rateLimitError);
    }

    // Only handle 401 errors and avoid infinite retry loops
    if (error.response?.status !== 401 || !originalRequest || originalRequest._retry) {
      // If a retried request (after token refresh) still got 401, the session
      // is truly expired. Trigger auth expiration to redirect the user to login
      // instead of silently swallowing the error and leaving the page broken.
      if (error.response?.status === 401 && originalRequest?._retry) {
        console.warn('[apiClient] Retry after token refresh still got 401 - triggering login redirect');
        b2cAuthService.handleAuthFailure();
      }
      return Promise.reject(error);
    }

    // Skip token refresh for auth endpoints to prevent loops during login
    if (originalRequest.url?.includes('/auth/')) {
      console.warn('[apiClient] 401 on auth endpoint, skipping refresh');
      return Promise.reject(error);
    }

    console.warn('[apiClient] 401 Unauthorized, attempting token refresh:', originalRequest.url);

    // If already refreshing, queue this request
    if (isRefreshingToken) {
      return new Promise((resolve, reject) => {
        failedRequestsQueue.push({ resolve, reject, config: originalRequest });
      });
    }

    originalRequest._retry = true;
    isRefreshingToken = true;

    try {
      // Attempt to refresh the token using b2cAuthService
      const newToken = await b2cAuthService.refreshToken();

      if (newToken) {
        console.log('[apiClient] Token refreshed successfully, retrying request');

        // Process queued requests with new token
        processQueue(newToken);

        // Retry the original request with new token
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return apiClient(originalRequest);
      } else {
        // Token refresh failed - session has expired
        console.warn('[apiClient] Token refresh failed, session expired');

        // Process queued requests with null (will reject them)
        processQueue(null);

        // The b2cAuthService.refreshToken() already triggers the auth expiration callback
        return Promise.reject(new Error('Session expired. Please log in again.'));
      }
    } catch (refreshError) {
      console.error('[apiClient] Token refresh error:', refreshError);

      // Process queued requests with null (will reject them)
      processQueue(null);

      return Promise.reject(refreshError);
    } finally {
      isRefreshingToken = false;
    }
  }
);

export default apiClient;
